import React, { useState, useEffect } from 'react'; // Import useEffect
import { useInView } from 'react-intersection-observer';
import { SparklesIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { useLocation } from 'react-router-dom'; // Import useLocation

import UrlConfig from '../../data/UrlConfig';
import { sendCodedLog } from '../../services/logService'; // Import sendCodedLog
import Common from '../../common/Common'; // Import Common

const API_URLS = UrlConfig;

const BasharTransmissionsInterest = () => {
  const [name, setName] = useState(localStorage.getItem('track_user_name') || '');
  const [email, setEmail] = useState(localStorage.getItem('track_user_email') || '');
  const [transmissionOfInterest, setTransmissionOfInterest] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const location = useLocation(); // Get the location object for logging

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');
    setError('');

    // Log cuando el usuario hace clic en submit
    sendCodedLog({
      message: 'Click en botón "Submit My Interest" en transmisiones de Bashar',
      event: 'bashar_interest_form_submit_en',
      ...Common.getCommonLogData(location)
    });

    name.trim() && localStorage.setItem('track_user_name', name.trim());
    email.trim() && localStorage.setItem('track_user_email', email.trim());
    
    // Basic frontend validation
    if (!name.trim() || !email.trim() || !transmissionOfInterest.trim()) {
      setError('Please enter your name, email, and what transmissions you are interested in.');
      setIsLoading(false);
      sendCodedLog({
        message: 'Error de validación frontend: campos requeridos vacíos',
        event: 'bashar_interest_form_validation_error_en',
        data: { errorType: 'required_fields_empty' },
        ...Common.getCommonLogData(location)
      });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      setIsLoading(false);
      sendCodedLog({
        message: 'Error de validación frontend: email inválido',
        event: 'bashar_interest_form_validation_error_en',
        data: { errorType: 'invalid_email' },
        ...Common.getCommonLogData(location)
      });
      return;
    }

    try {
      const response = await fetch(API_URLS.leads_add, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          nick: name,
          page: 'BasharTransmissionsInterestPage',
          folder: 'going-gamma-full',
          section: 'full-transmissions-interest-form',
          status: 'BasharFullTransmissionsInterest',
          newsletter: ['bashar-full-transmissions'],
          message: transmissionOfInterest,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Thank you for your interest!');
        setName('');
        setEmail('');
        setTransmissionOfInterest('');
        setSubmitted(true);
        sendCodedLog({
          message: 'Formulario de interés en Bashar enviado exitosamente',
          event: 'bashar_interest_form_success_en',
          ...Common.getCommonLogData(location)
        });
      } else {
        throw new Error(data.message || 'An error occurred while processing your request.');
      }
    } catch (err) {
      setError(err.message);
      sendCodedLog({
        message: `Error al enviar formulario de interés en Bashar: ${err.message}`,
        event: 'bashar_interest_form_backend_error_en',
        data: { errorMessage: err.message },
        ...Common.getCommonLogData(location)
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen font-sans flex flex-col">
      {/* Gradient background similar to HomePage */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95"></div>

      <main
        ref={ref}
        className={`relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center
          ${inView ? "fade-in-animation" : "opacity-0"}
        `}
      >
        <h1 className="text-3xl sm:text-4xl font-bold text-center mb-4">
          Interested in Full Bashar Transmissions?
        </h1>
        <p className="text-base sm:text-lg text-gray-400 text-center max-w-2xl mb-10">
          Tell us about your interest in Bashar's complete sessions. Fill out the form below, and we'll reach out with more information.
        </p>

        <div className="w-full max-w-md bg-black/30 border border-gray-700 shadow-lg shadow-gray-700/30 rounded-xl p-6 sm:p-8">
          {submitted ? (
            <div className="text-center text-green-400 p-6 bg-green-900/30 border border-green-700 rounded-lg">
              <CheckCircleIcon className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p className="text-xl font-semibold">{message}</p>
              <p className="text-lg">We will contact you soon with more details about full Bashar transmissions.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-300 text-sm font-medium mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-3 rounded-md bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
                  placeholder="John Doe"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-300 text-sm font-medium mb-2">
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 rounded-md bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
                  placeholder="your.email@example.com"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label htmlFor="transmission" className="block text-gray-300 text-sm font-medium mb-2">
                  Which transmission or topic are you interested in?
                </label>
                <textarea
                  id="transmission"
                  value={transmissionOfInterest}
                  onChange={(e) => setTransmissionOfInterest(e.target.value)}
                  rows="4"
                  className="w-full p-3 rounded-md bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-400"
                  placeholder="e.g., specific teachings on abundance, relationships, future events, etc."
                  required
                  disabled={isLoading}
                ></textarea>
              </div>

              {(error || message) && (
                  <p className={`text-sm text-center ${error ? 'text-red-400' : 'text-green-400'}`}>
                      {error || message}
                  </p>
              )}

              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-sky-500 to-purple-600 text-white font-semibold text-lg
                           hover:from-sky-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-400
                           transition-all duration-300 ease-in-out shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                    <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                    </>
                ) : (
                    <>
                        Submit My Interest <SparklesIcon className="w-5 h-5" />
                    </>
                )}
              </button>
            </form>
          )}
        </div>
      </main>

      <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
        © {new Date().getFullYear()} Gus • Aguiar | On a mission to uplift and transform.
      </footer>
    </div>
  );
};

export default BasharTransmissionsInterest;